import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { AuthState } from '../redux/authSlice';
import { RootState } from '../redux/store';
import { menuRoutes } from '../router/menu';
import { CommunicationService } from '../services/communication/communicationService';
import { COMMUNICATION_ALERTS_REFRESH_EVENT } from '../utils/communicationAlerts';

interface CommunicationAlertsContextType {
    hasPending: boolean;
    isEligible: boolean;
    loading: boolean;
    pendingCount: number;
    refreshPendingSummary: () => Promise<void>;
}

const CommunicationAlertsContext = createContext<CommunicationAlertsContextType | undefined>(undefined);

const FAST_POLLING_INTERVAL = 15000;
const DEFAULT_POLLING_INTERVAL = 60000;

export const CommunicationAlertsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const location = useLocation();
    const { isAuthenticated, user }: AuthState = useSelector((state: RootState) => state.auth);

    const [eligibilityOverride, setEligibilityOverride] = useState<boolean | null>(null);
    const [pendingCount, setPendingCount] = useState(0);
    const [loading, setLoading] = useState(false);

    const hasEligibilityFlag = typeof user?.departmentHandlesCommunications === 'boolean';
    const canResolveEligibility = Boolean(isAuthenticated && user?.department);
    const isEligible = Boolean(
        canResolveEligibility && (user?.departmentHandlesCommunications === true || eligibilityOverride === true)
    );
    const shouldFetchSummary = Boolean(
        canResolveEligibility && (user?.departmentHandlesCommunications === true || !hasEligibilityFlag || eligibilityOverride === true)
    );
    const isCommunicationsRoute = location.pathname.includes(menuRoutes.calls.communications);
    const pollingInterval = hasPendingOrCommunicationsRoute(pendingCount > 0, isCommunicationsRoute)
        ? FAST_POLLING_INTERVAL
        : DEFAULT_POLLING_INTERVAL;

    const refreshPendingSummary = useCallback(async () => {
        if (!shouldFetchSummary) {
            setEligibilityOverride(null);
            setPendingCount(0);
            return;
        }

        setLoading(true);

        try {
            const service = new CommunicationService();
            const response = await (await service.getPendingSummary()).getResponseData();

            if (response?.success) {
                const enabled = Boolean(response.data?.enabled);
                setEligibilityOverride(enabled);
                setPendingCount(enabled ? Number(response.data?.pendingCount || 0) : 0);
            }
        } catch (_error) {
            setPendingCount(0);
        } finally {
            setLoading(false);
        }
    }, [shouldFetchSummary]);

    useEffect(() => {
        setEligibilityOverride(user?.departmentHandlesCommunications === true ? true : null);

        if (!canResolveEligibility) {
            setPendingCount(0);
            setLoading(false);
        }
    }, [canResolveEligibility, user?.departmentHandlesCommunications, user?.id]);

    useEffect(() => {
        if (!shouldFetchSummary) {
            setPendingCount(0);
            setLoading(false);
            return;
        }

        void refreshPendingSummary();
    }, [refreshPendingSummary, shouldFetchSummary, user?.id]);

    useEffect(() => {
        if (!shouldFetchSummary) {
            return;
        }

        const runRefresh = () => {
            if (document.visibilityState !== 'visible' || !navigator.onLine) {
                return;
            }

            void refreshPendingSummary();
        };

        const intervalId = window.setInterval(runRefresh, pollingInterval);
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                runRefresh();
            }
        };
        const handleOnline = () => runRefresh();
        const handleExternalRefresh = () => runRefresh();

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('online', handleOnline);
        window.addEventListener(COMMUNICATION_ALERTS_REFRESH_EVENT, handleExternalRefresh);

        return () => {
            window.clearInterval(intervalId);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('online', handleOnline);
            window.removeEventListener(COMMUNICATION_ALERTS_REFRESH_EVENT, handleExternalRefresh);
        };
    }, [pollingInterval, refreshPendingSummary, shouldFetchSummary]);

    const value = useMemo<CommunicationAlertsContextType>(() => ({
        hasPending: pendingCount > 0,
        isEligible,
        loading,
        pendingCount,
        refreshPendingSummary,
    }), [isEligible, loading, pendingCount, refreshPendingSummary]);

    return (
        <CommunicationAlertsContext.Provider value={value}>
            {children}
        </CommunicationAlertsContext.Provider>
    );
};

const hasPendingOrCommunicationsRoute = (hasPending: boolean, isCommunicationsRoute: boolean) => {
    return hasPending || isCommunicationsRoute;
};

export const useCommunicationAlerts = () => {
    const context = useContext(CommunicationAlertsContext);

    if (!context) {
        throw new Error('useCommunicationAlerts must be used within a CommunicationAlertsProvider');
    }

    return context;
};
